<template>
  <div :class="classes.wrapper">
    <Competitors
      v-if="useCompetitors"
      :eventId="eventId"
      :class="[classes.competitors, 'competitors']"
    />

    <LinkWrapper
      v-else
      :href="eventLink"
      target="_blank"
      :class="[classes.eventName, layoutClass, 'event']"
      @click="() => handleClick()"
    >
      <FontFillText
        :text="eventName"
        styleGroup="Event Name"
        :getFont="fixtureNameStyle.get"
        :setFont="fixtureNameStyle.set"
        :minSize="minSize"
        :lineHeight="lineHeight"
        :alignment="alignment"
        :justifyContent="justifyContent"
      />
    </LinkWrapper>
  </div>
</template>

<script lang="ts">
import { defineComponent, computed, PropType, ComputedRef } from "vue";
import { createUseStyles } from "@fresh8/vue-jss/src";
import Competitors from "./Competitors.vue";
import LinkWrapper from "./LinkWrapper.vue";

import { composeEvent } from "@fresh8/ad-data-composition/src/betData";
import { Brand } from "@fresh8/types/src";

import { injectCardNumber, useAnalytics } from "../hooks/useAnalytics";
import FontFillText from "./FontFillText.vue";

import {
  injectTemplateSize,
  MOBILE_LEADERBOARD
} from "@fresh8/ad-data-composition/src/useTemplateSize";
import { SportEvent } from "@fresh8/types/src/betData";

const useStyles = createUseStyles({
  wrapper: {
    display: "flex",
    flexDirection: "column",
    justifyContent: props => props.justifyContent,
    alignItems: props => props.alignment
  },
  eventName: {
    display: "flex",
    flex: "1 1 0px",
    height: "inherit",
    width: "100%",
    justifyContent: props => props.justifyContent,
    alignItems: props => props.alignment,
    [`&.${MOBILE_LEADERBOARD.name}`]: {
      minHeight: "18px",
      height: "18px",
      maxHeight: "18px"
    }
  },
  competitors: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    "& > .versus": {
      fontSize: "11px",
      margin: "0 5px",
      maxWidth: "10px",
      textAlign: "center"
    },
    "& > .team-name": {
      fontSize: "13px",
      textAlign: "center"
    }
  }
});

export default defineComponent({
  name: "EventNameFitted",
  components: {
    Competitors,
    LinkWrapper,
    FontFillText
  },
  props: {
    eventId: { type: String, required: true },
    forceEventName: { type: Boolean, default: false },
    alignment: {
      type: String as PropType<"flex-end" | "flex-start" | "center">,
      default: "center"
    },
    justifyContent: {
      type: String as PropType<"flex-end" | "flex-start" | "center">,
      default: "center"
    },
    lineHeight: {
      type: Number,
      default: 1.2
    },
    minSize: {
      type: Number,
      default: 8
    }
  },
  setup(props) {
    const templateContext = injectTemplateSize();
    const reactiveStyles = computed(() => ({
      alignment: props.alignment,
      justifyContent: props.justifyContent
    }));
    const classes = useStyles(reactiveStyles);
    const event = composeEvent(props.eventId) as ComputedRef<SportEvent>;
    const cardNumber = injectCardNumber();
    const { handleClick } = useAnalytics(
      { eventId: props.eventId, cardNumber: cardNumber },
      "Event Name"
    );

    const useCompetitors = computed(() => {
      return (
        !props.forceEventName &&
        event?.value?.competitors.home.name.full?.length > 0 &&
        event?.value?.competitors.away.name.full?.length > 0
      );
    });

    return {
      classes,
      useCompetitors,
      eventLink: computed(() => event.value.eventUrl),
      eventName: computed(() => event.value.name),
      handleClick,
      layoutClass: computed(() => templateContext?.size.value.name),
      fixtureNameStyle: {
        get: (brand: Brand | undefined) =>
          brand && brand.adTemplate.market.teamNameFont,
        set: (brand: Brand, value: string) =>
          (brand.adTemplate.market.teamNameFont = value)
      }
    };
  }
});
</script>
